import { styled } from '@mui/material';
import { shouldForwardProp as sysShouldForwardProp } from '@mui/system';
import { ChevronRight, CustomerAvatarBusiness, CustomerAvatarResident } from '@o2/icons';
import { Box, Button, ButtonProps, Typography } from '@o2/ui';
import colors from '@o2/ui/theme/colors';
import { clsx } from 'clsx';
import { ComponentPropsWithoutRef, ComponentPropsWithRef, ElementType, forwardRef, ReactNode } from 'react';
import userPopperLinkClasses, { useUserPopperLinkUtilityClasses } from './userPopperLinkClasses';

export type UserPopperLinkProps<T extends ElementType = 'button'> = Omit<
  ButtonProps<T>,
  'children' | 'startIcon' | 'endIcon' | 'color'
> & {
  /**
   * Link icon - can use resident, business or custom icon (ReactNode)
   */
  icon?: 'resident' | 'business' | ReactNode;

  /**
   * Link text
   */
  label: string;

  /**
   * Link color (default or internal)
   */
  color?: 'default' | 'internal';

  /**
   * What component to render this component as (inheriting props).
   * Useful when combining with e.g. react-router.
   */
  as?: T;

  ref?: ComponentPropsWithRef<T>['ref'];
} & ComponentPropsWithoutRef<T>;

const StyledButton = styled(Button, {
  name: 'O2UserPopperLink',
  slot: 'root',
  shouldForwardProp: (prop) => sysShouldForwardProp(prop) && prop !== 'linkColor',
})<{ linkColor: UserPopperLinkProps['color'] }>(({ theme, linkColor }) => ({
  width: '100%',
  border: 'none',
  borderRadius: 0,
  padding: theme.spacing(2),
  display: 'flex',
  flexDirection: 'row',
  gap: theme.spacing(2),
  margin: 0,
  textAlign: 'left',
  borderTop: `1px solid ${theme.palette.divider}`,
  '&:first-of-type': {
    borderTop: `none`,
    '&:hover': {
      borderTop: 'none',
    },
  },
  textDecoration: 'none',
  backgroundColor: linkColor === 'internal' ? theme.palette.background.internal : theme.palette.common.white,
  '&:hover': {
    border: 'none',
    borderRadius: 0,
    borderTop: `1px solid ${theme.palette.divider}`,
    textDecoration: 'none',
    backgroundColor: theme.palette.background.primary,
  },
  fontSize: theme.typography.pxToRem(14),
  fontWeight: theme.typography.fontWeightRegular,
  color: colors.solidGray[900],
  [`& > .${userPopperLinkClasses.arrow}`]: {
    color: colors.solidGray[400],
    transition: 'color 250ms',
  },
  [`&:hover > .${userPopperLinkClasses.arrow}`]: {
    color: theme.palette.primary.main,
  },
}));

const IconBox = styled(Box, {
  name: 'O2UserPopperLink',
  slot: 'icon',
})(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  textAlign: 'center',
  width: '32px',
  fontSize: theme.typography.pxToRem(32),
}));

const ArrowBox = styled(Box, {
  name: 'O2UserPopperLink',
  slot: 'arrow',
})(() => ({
  display: 'flex',
  justifyContent: 'center',
  textAlign: 'center',
}));

const Text = styled(Typography, {
  name: 'O2UserPopperLink',
  slot: 'text',
})(() => ({
  whiteSpace: 'normal',
  overflowWrap: 'break-word',
}));

const UserPopperLink = forwardRef(
  <T extends ElementType = 'button'>(
    { icon, color, onClick, label, classes, ...other }: UserPopperLinkProps<T>,
    ref: UserPopperLinkProps<T>['ref']
  ) => {
    const utilityClasses = useUserPopperLinkUtilityClasses(classes);

    return (
      <StyledButton className={clsx(utilityClasses.root)} ref={ref} onClick={onClick} linkColor={color} {...other}>
        {icon && (
          <IconBox className={clsx(utilityClasses.icon)}>
            {icon === 'resident' ? (
              <CustomerAvatarResident width={32} />
            ) : icon === 'business' ? (
              <CustomerAvatarBusiness width={32} />
            ) : (
              icon
            )}
          </IconBox>
        )}
        <Text className={clsx(utilityClasses.text)} flexGrow={1}>
          {label}
        </Text>
        <ArrowBox className={clsx(utilityClasses.arrow)}>
          <ChevronRight />
        </ArrowBox>
      </StyledButton>
    );
  }
);

UserPopperLink.displayName = 'UserPopperLink';

export default UserPopperLink;
